import { Fragment, FunctionComponent, ReactNode, useCallback, useMemo, useState } from "react";
import { Tab } from "@headlessui/react";
import clsx from "clsx";

interface TabsProps {
  className?: string;
  tabs: string[] | { label: string; disabled?: boolean; hidden?: boolean }[];
  selectedIndex?: number;
  onChange?: (index: number) => void;
  children?: ReactNode | ((selectedIndex: number) => ReactNode);
  customColor?: string;
}
const Tabs: FunctionComponent<TabsProps> = ({
  className,
  tabs,
  selectedIndex: controlledSelectedIndex,
  onChange,
  children,
  customColor,
}) => {
  const formattedTabs = useMemo(
    () =>
      tabs.map((tab) =>
        typeof tab === "string"
          ? { label: tab, disabled: false, hidden: false }
          : { label: tab.label, disabled: !!tab.disabled, hidden: !!tab.hidden },
      ),
    [tabs],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const handleChange = useCallback(
    (index: number) => {
      if (controlledSelectedIndex === undefined) setSelectedIndex(index);
      if (onChange) onChange(index);
    },
    [controlledSelectedIndex, onChange],
  );

  return (
    <Tab.Group
      as="div"
      className={className}
      selectedIndex={
        controlledSelectedIndex !== undefined ? controlledSelectedIndex : selectedIndex
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChange={handleChange}
    >
      <Tab.List
        as="nav"
        className="tw-shrink-0 tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-b tw-border-gray-200"
      >
        <ul className="-tw-mb-px tw-flex tw-space-x-8 tw-list-none tw-p-0">
          {formattedTabs.map(
            ({ label, hidden }, index) =>
              !hidden && (
                <Tab as={Fragment} key={index}>
                  {({ selected }) => (
                    <li
                      className={clsx(
                        "tw-cursor-pointer tw-border-solid tw-border-transparent tw-whitespace-nowrap tw-py-4 tw-px-1 tw-border-b-2 tw-font-medium tw-text-sm focus:tw-outline-none",
                        selected
                          ? "tw-text-indigo-800 tw-border-b-indigo-800"
                          : "tw-text-gray-600 hover:tw-text-gray-700 hover:tw-border-b-gray-300",
                      )}
                      style={
                        selected && customColor
                          ? {
                              color: customColor,
                              borderBottomColor: customColor,
                            }
                          : {}
                      }
                    >
                      {label}
                    </li>
                  )}
                  {/* {label} */}
                </Tab>
              ),
          )}
        </ul>
      </Tab.List>
      <Tab.Panels as={Fragment}>
        {typeof children === "function"
          ? children(
              controlledSelectedIndex !== undefined ? controlledSelectedIndex : selectedIndex,
            )
          : children}
      </Tab.Panels>
    </Tab.Group>
  );
};

export default Tabs;
